/* ***************************************************************** */
/*                                                                   */
/* Licensed Materials - Property of Merative                         */
/*                                                                   */
/* (C) Copyright Merative Corp. 2025                                 */
/*                                                                   */
/* ***************************************************************** */
import React, {useEffect} from 'react';
import { useDispatch, useSelector } from 'react-redux';
import {useHistory, useLocation} from 'react-router-dom';
import TabbedContainer from "../../../../../../components/common/TabbedContainer";
import {NeofaxPedsMonographPicklist} from './NeofaxPedsMonographPicklist';
import {
    selectNeonatalDrugs,
    selectPediatricDrugs,
    selectedTabIndex,
    setSelectedTabIndex, selectNeonatalLoadingState, selectPediatricLoadingState, selectIsStateCleared
} from "../redux/neofax_search_redux";
import { get } from "../../../../../../utils";
import {
    NEO_PED_DRUG_ID_KEY,
    NEOFAX_FORMULA_PICKLIST_ID, NEOFAX_PEDIATRICS_ERROR_TYPE,
    NEOFAX_PICKLIST_ID, NEONATAL, NO_PAGE_VIEW_TRACKING,
    PEDIATRICS_PICKLIST_ID, RESULT_TYPE_NEOFAX, RESULT_TYPE_PEDIATRICS
} from "../../../../common/constants";
import { computeInitialPicklistId, updateTabParamInUrl} from "../../../../utils";
import {NeoPedMonographComponent} from "../../MonographPage";
import {TitleHeader} from "../../../../common/components";
import {handleErrorStatuses} from "../../../../../../utils/internal-error-handler";
import {SUCCEED_STATUS} from "../../../../../../components/response-loading/redux/response-loading-redux";

export const NeofaxPedsMonographSearchPanel = ({shouldUseDefaults = false}) => {
    const dispatch = useDispatch();
    const location = useLocation();
    const history = useHistory();
    
    const login_page = useSelector((state) => get(state, 'login_page', {}));
    const currentTabIndex = useSelector(selectedTabIndex);
    const neonatal_drug_picklist_state = useSelector(selectNeonatalDrugs);
    const pediatrics_drug_picklist_state = useSelector(selectPediatricDrugs);

    const hasNeonatalSubscription = get(login_page, 'mdxAuthenticationJson.neonatalSubscription', 'no') === 'yes';
    const hasPediatricSubscription = get(login_page, 'mdxAuthenticationJson.pediatricsSubscription', 'no') === 'yes';

    const searchParams = new URLSearchParams(location.search);
    const documentId = searchParams.get("documentId");
    const isSearchPage = location.pathname !== "/neoped/monograph";

    const tabFromUrl = searchParams.get("tab");
    const wantedPicklistId = computeInitialPicklistId(tabFromUrl, hasNeonatalSubscription, hasPediatricSubscription);

    const neonatal_data_array = neonatal_drug_picklist_state?.data_array || [];
    const pediatrics_data_array = pediatrics_drug_picklist_state?.data_array || [];
    const isNeonatalDrugInList = documentId && neonatal_data_array.some(item => item[1] === documentId);
    const isPediatricsDrugInList = documentId && pediatrics_data_array.some(item => item[1] === documentId);
    const neonatalLoadingState = useSelector(selectNeonatalLoadingState) || "";
    const pediatricsLoadingState = useSelector(selectPediatricLoadingState) || ""
    const isStateCleared = useSelector(selectIsStateCleared);

    useEffect(() => {
        if (isSearchPage || !documentId) return;

        const switchToTab = (isNeonatal) => {
            let tabIndex = 0;
            if (!isNeonatal && hasNeonatalSubscription) {
                tabIndex = 1;
            }
            
            const tabType = isNeonatal ? RESULT_TYPE_NEOFAX : RESULT_TYPE_PEDIATRICS;
            
            dispatch(setSelectedTabIndex(tabIndex));
            updateTabParamInUrl(tabType, history);
        };

        const isOnNeonatalTabWithValidDrug = currentTabIndex === 0 && hasNeonatalSubscription && isNeonatalDrugInList;
        const isOnPediatricTabWithValidDrug = (currentTabIndex === 1 || !hasNeonatalSubscription) && hasPediatricSubscription && isPediatricsDrugInList;

        if (isOnNeonatalTabWithValidDrug || isOnPediatricTabWithValidDrug) {
            const isNeonatal = currentTabIndex === 0 && hasNeonatalSubscription;
            switchToTab(isNeonatal);
            return;
        }

        if (isNeonatalDrugInList && hasNeonatalSubscription) {
            switchToTab(true);
            return;
        }
        
        if (isPediatricsDrugInList && hasPediatricSubscription && !shouldUseDefaults) {
            switchToTab(false);
        }
    }, [documentId, isNeonatalDrugInList, isPediatricsDrugInList, isSearchPage, history,
        hasNeonatalSubscription, hasPediatricSubscription, dispatch, currentTabIndex, shouldUseDefaults]);

    
    const getTabIndex = () => {
        if (!hasPediatricSubscription) return 0;
        if (hasNeonatalSubscription) return 1;
        return 0;
    };

    useEffect(() => {
        if (wantedPicklistId) {
            const tabIndex = wantedPicklistId === NEOFAX_FORMULA_PICKLIST_ID ? 0 : 1;
            dispatch(setSelectedTabIndex(tabIndex));
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    useEffect(() => {
        const neonatalStateFailed = !hasNeonatalSubscription && !isNeonatalDrugInList && pediatricsLoadingState === SUCCEED_STATUS;
        const pediatricsStateFailed = !hasPediatricSubscription && !isPediatricsDrugInList && neonatalLoadingState === SUCCEED_STATUS;
        const shouldShowSubscriptionPopup = tabFromUrl ? tabFromUrl === RESULT_TYPE_NEOFAX ? neonatalStateFailed : pediatricsStateFailed : false;
        const bothSubscriptionFailed = (!hasNeonatalSubscription && !hasPediatricSubscription);
        if ((!isSearchPage && !isStateCleared && shouldShowSubscriptionPopup) || bothSubscriptionFailed) {
            let mutable_props = {history};
            mutable_props.error_type = NEOFAX_PEDIATRICS_ERROR_TYPE;
            const getLoginState = () => ({login_page: login_page});
            setTimeout(() => {
                handleErrorStatuses(403, mutable_props, dispatch, getLoginState);
            }, 0);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [tabFromUrl, pediatricsLoadingState, neonatalLoadingState]);

    const searchDrugTypeTabs = [
        {
            label: 'Neonatal',
            content: <>
                <NeofaxPedsMonographPicklist 
                    drugTypeName={NEONATAL}
                    picklistId={NEOFAX_PICKLIST_ID}
                />
            </>,
            onClick: () => {
                sessionStorage.setItem(NO_PAGE_VIEW_TRACKING, true);
                dispatch(setSelectedTabIndex(0));
                updateTabParamInUrl(RESULT_TYPE_NEOFAX, history);
            }
        },
        {
            label: 'Pediatric',
            content: <>
                <NeofaxPedsMonographPicklist 
                    drugTypeName="Pediatric"
                    picklistId={PEDIATRICS_PICKLIST_ID}
                />
            </>,
            onClick: () => {
                sessionStorage.setItem(NO_PAGE_VIEW_TRACKING, true);
                dispatch(setSelectedTabIndex(getTabIndex()));
                updateTabParamInUrl(RESULT_TYPE_PEDIATRICS, history);
            }
        }
    ];

    const monographDrugTypeTabs = [
        {
            label: 'Neonatal',
            disabled: !isNeonatalDrugInList,
            content: <>
                {isNeonatalDrugInList &&
                  <NeoPedMonographComponent isNeonatal={true} location={location}/>}
            </>,
            onClick: () => {
                dispatch(setSelectedTabIndex(0));
                updateTabParamInUrl(RESULT_TYPE_NEOFAX, history);
                if (sessionStorage.getItem(NEO_PED_DRUG_ID_KEY) !== documentId) {
                    sessionStorage.setItem(NEO_PED_DRUG_ID_KEY, documentId);
                }
            }
        },
        {
            label: 'Pediatric',
            disabled: !isPediatricsDrugInList,
            content: <>
                {isPediatricsDrugInList &&
                  <NeoPedMonographComponent isNeonatal={false} location={location}/>}
            </>,
            onClick: () => {
                dispatch(setSelectedTabIndex(getTabIndex()));
                updateTabParamInUrl(RESULT_TYPE_PEDIATRICS, history);
                if (sessionStorage.getItem(NEO_PED_DRUG_ID_KEY) !== documentId) {
                    sessionStorage.setItem(NEO_PED_DRUG_ID_KEY, documentId);
                }
            }
        }
    ];

    return (
        <>
            <TitleHeader
              subTitle="NeoFax® / Pediatrics"
              mainTitle="Drug Monographs"
            />
            {isSearchPage && <p className="info-message-top">
                Enter the first few letters to select a drug name.
            </p>}
            {(hasNeonatalSubscription || hasPediatricSubscription) &&
              <>
                  <TabbedContainer
                    tabs={searchDrugTypeTabs.filter((tab, index) =>
                      (index === 0 && hasNeonatalSubscription) ||
                      (index === 1 && hasPediatricSubscription)
                    )}
                    className={`search-tabs ${!isSearchPage && (isNeonatalDrugInList || isPediatricsDrugInList) ? 'show-no-tabs' : ''}`}
                    defaultSelectedIndex={0}
                    ariaLabel="Neonatal and Pediatric tabs"
                    tabContentClassName="tabs-inner-content"
                    contained={true}
                    preSelectedIndex={!hasNeonatalSubscription ? 0 : currentTabIndex}
                    shouldShowTabPanel={isSearchPage || (!isNeonatalDrugInList && !isPediatricsDrugInList)}
                  />
                  {!isSearchPage && <TabbedContainer
                    tabs={monographDrugTypeTabs.filter((tab, index) =>
                      (index === 0 && hasNeonatalSubscription) ||
                      (index === 1 && hasPediatricSubscription)
                    )}
                    className="monograph-tabs"
                    defaultSelectedIndex={0}
                    ariaLabel="Neonatal and Pediatric tabs"
                    tabContentClassName="tabs-inner-content"
                    contained={true}
                    preSelectedIndex={!hasNeonatalSubscription ? 0 : currentTabIndex}
                    shouldShowTabPanel={isNeonatalDrugInList || isPediatricsDrugInList}
                  />}
              </>
            }
        </>
    );
};
